import type {
  FmConfig,
  FmTarget,
  NoiseConfig,
  OscConfig,
  SynthPreset,
} from "../types";

/**
 * Voice sections added after the original two-oscillator preset shape.
 * They are optional on `SynthPreset` and default to inaudible, so presets
 * written before they existed — including saved projects and localStorage —
 * keep rendering exactly as they did.
 */

/** Phase-modulation index ceiling, in cycles of carrier phase offset. */
export const MAX_FM_DEPTH = 8;

export function silentOsc3(): OscConfig {
  return { wave: "sine", octave: 0, semis: 0, detuneCents: 0, level: 0 };
}

export function noNoise(): NoiseConfig {
  return { level: 0 };
}

export function noFm(): FmConfig {
  return { target: "osc1", fmDepth: 0, amDepth: 0, velocityToFm: 0 };
}

/** A preset with every optional voice section filled in. */
export type ResolvedSynthPreset = SynthPreset & {
  osc3: OscConfig;
  noise: NoiseConfig;
  fm: FmConfig;
};

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback;
}

function resolveOsc(osc: OscConfig | undefined, fallback: OscConfig): OscConfig {
  if (!osc) {
    return fallback;
  }
  return {
    wave: osc.wave ?? fallback.wave,
    octave: clamp(osc.octave, -4, 4, fallback.octave),
    semis: clamp(osc.semis, -24, 24, fallback.semis),
    detuneCents: clamp(osc.detuneCents, -1200, 1200, fallback.detuneCents),
    level: clamp(osc.level, 0, 1, fallback.level),
    ...(osc.pwm === undefined ? {} : { pwm: clamp(osc.pwm, 0.05, 0.95, 0.5) }),
  };
}

function resolveFmTarget(target: unknown): FmTarget {
  return target === "osc2" || target === "both" ? target : "osc1";
}

/**
 * Fill in optional sections and clamp to renderable ranges. Presets arrive as
 * raw JSON from project files and localStorage, so nothing here may be trusted.
 */
export function normalizePreset(preset: SynthPreset): ResolvedSynthPreset {
  const fm = preset.fm;
  return {
    ...preset,
    osc3: resolveOsc(preset.osc3, silentOsc3()),
    noise: { level: clamp(preset.noise?.level, 0, 1, 0) },
    fm: {
      target: resolveFmTarget(fm?.target),
      fmDepth: clamp(fm?.fmDepth, 0, MAX_FM_DEPTH, 0),
      amDepth: clamp(fm?.amDepth, 0, 1, 0),
      velocityToFm: clamp(fm?.velocityToFm, 0, 1, 0),
    },
  };
}
